import { useObserver, useSubtree } from "subtree.js/react";
import { WebsiteSectionActions, WebsiteSectionState } from "./website-section-model";

/** "a contact email", "a contact email and a returns policy", "x, y and z". */
function listMissing(items: string[]): string {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function WebsiteSection() {
  const state = useSubtree(WebsiteSectionState);
  const actions = useSubtree(WebsiteSectionActions);
  const observer = useObserver();
  const store = observer.watch(state.store);
  const mode = observer.watch(state.mode);
  const websiteUrl = observer.watch(state.websiteUrl);
  const slug = observer.watch(state.slug);
  const contactEmail = observer.watch(state.contactEmail);
  const returnsPolicy = observer.watch(state.returnsPolicy);
  const hostedErrors = observer.watch(state.hostedErrors);
  const slugProblem = observer.watch(state.slugProblem);
  const hasMappableLocation = observer.watch(state.hasMappableLocation);
  const error = observer.watch(state.error);
  const saved = observer.watch(state.saved);
  const busy = observer.watch(state.busy);

  if (!store) return null;

  /**
   * What the hosted page still lacks before Stripe will be given its URL.
   *
   * Mirrors `ShopPageOps.isPublishable` on the server, which is the authority —
   * this exists so the merchant is told what to fix rather than discovering it
   * when verification stalls. Opening hours are absent on purpose: good for
   * customers, not something a reviewer asks for.
   */
  const descriptionMissing = !store.description?.trim();
  const missing = [
    descriptionMissing && "a description of what you sell",
    !contactEmail.trim() && "a contact email",
    !returnsPolicy.trim() && "a returns policy",
    !hasMappableLocation && "an address",
  ].filter((item): item is string => typeof item === "string");

  const slugChanged = mode === "hosted" && Boolean(store.slug) && slug !== store.slug;
  const liveUrl = store.websiteUrl ?? (store.slug ? `Your shop page: /shop/${store.slug}` : null);

  return (
    <section className="panel p-6 sm:p-8">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-white">Website</h2>
        <p className="mt-1 text-sm text-muted">
          The address Stripe checks when it verifies your shop, and the link customers follow.
        </p>
        {liveUrl && <p className="mt-2 break-all font-mono text-xs text-muted">{liveUrl}</p>}
      </div>

      {error && <p className="note note-danger mb-4 text-danger">{error}</p>}
      {saved && !error && <p className="note note-accent mb-4 text-accent">Saved.</p>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => actions.setMode("own")}
          className={mode === "own" ? "btn-primary btn-sm" : "btn-secondary btn-sm"}
        >
          My own website
        </button>
        <button
          type="button"
          onClick={() => actions.setMode("hosted")}
          className={mode === "hosted" ? "btn-primary btn-sm" : "btn-secondary btn-sm"}
        >
          A page you host for me
        </button>
      </div>

      <div className="mt-4">
        {mode === "own" ? (
          <input
            type="url"
            inputMode="url"
            value={websiteUrl}
            onChange={(event) => actions.setWebsiteUrl(event.target.value)}
            placeholder="cardcorner.com"
            className="field"
            aria-label="Your website address"
          />
        ) : (
          <>
            <div className="flex items-center gap-1 rounded-btn border border-line bg-surface px-3 py-2.5">
              <span className="shrink-0 font-mono text-xs text-faint">/shop/</span>
              <input
                value={slug}
                onChange={(event) => actions.setSlug(event.target.value)}
                placeholder="card-corner"
                className="min-w-0 flex-1 bg-transparent font-mono text-xs text-white outline-none"
                aria-label="Your shop page address"
              />
            </div>
            {slugProblem && <p className="field-error">{slugProblem}</p>}
            {/* Only when they are actually moving it. Warning about a broken
                link on a page they opened to change something else would be
                noise. */}
            {slugChanged && !slugProblem && (
              <p className="mt-2 text-xs leading-relaxed text-warn">
                Changing this breaks the old link everywhere it has been shared or printed, and Stripe will re-check the
                new address.
              </p>
            )}

            <div className="mt-5 flex flex-col gap-3.5 border-t border-line pt-5">
              <p className="text-[13px] font-medium text-white">What the page shows</p>

              <div>
                <label htmlFor="contactEmail" className="label">
                  Contact email
                </label>
                <input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(event) => actions.setContactEmail(event.target.value)}
                  placeholder="hello@cardcorner.com"
                  className="field"
                />
                {hostedErrors.contactEmail && <p className="field-error">{hostedErrors.contactEmail}</p>}
              </div>

              {/* Opening hours are not here: they belong to a branch, and are
                  edited with the rest of that branch's details in Locations. */}
              <div>
                <label htmlFor="returnsPolicy" className="label">
                  Returns &amp; refunds
                </label>
                <textarea
                  id="returnsPolicy"
                  value={returnsPolicy}
                  onChange={(event) => actions.setReturnsPolicy(event.target.value)}
                  rows={3}
                  placeholder="Returns accepted within 14 days on unopened product, with a receipt."
                  className="field resize-y"
                />
                {hostedErrors.returnsPolicy ? (
                  <p className="field-error">{hostedErrors.returnsPolicy}</p>
                ) : (
                  <p className="mt-1 text-xs leading-relaxed text-faint">
                    Card networks require this on any website used to verify a business.
                  </p>
                )}
              </div>
            </div>

            {/* Named individually rather than as "incomplete": a merchant who is
                told what is missing can fix it, and this is the difference
                between Stripe checking their page and never seeing it. */}
            {missing.length > 0 && (
              <p className="note note-warn mt-4 text-xs leading-relaxed text-warn">
                Stripe won't be given this page until it has {listMissing(missing)}. Until then we describe your shop to
                Stripe instead — the page still works for customers.
                {/* The two fields above are refused on save, so only the ones
                    owned by another section can still be missing here — say
                    where, or the merchant is told to fix something with no
                    field in front of them. */}
                {descriptionMissing && " The description lives in Shop details, at the top of this page."}
              </p>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={actions.save}
        disabled={busy || Boolean(slugProblem)}
        className="btn-primary btn-sm mt-4"
      >
        {busy ? "Saving…" : "Save"}
      </button>
    </section>
  );
}
